import React, { useContext, useState } from 'react'
import {
  Sheet,
  BodyText,
  CaptionText,
  Card,
  Button,
  Badge,
  HeadingText,
  Alert,
} from '@paypalcorp/pp-react'

//Project imports
import AcceptInvitation from './sections/accept-invitation'
import {
  calculateExpiryTimeInHours,
  calculateExpiryTimeInDaysHours,
  formatDate,
} from '../../utils/transformations'
import {
  Context as WorldReadyContext,
  Message,
} from '@paypalcorp/worldready-react'
import {
  recordCplEnd,
  recordCplStart,
  recordDynamicEvent,
} from '../common/analytics/utils'
import Spinner from '../common/spinner'
import axiosInstance from '../../utils/axios-util'
import { useApplicationContext } from '../../context/ApplicationContext'
import UpgradeRequired from './sections/upgrade-required'
import { FPTI_COMPONENTS_CATEGORIES } from '../../common/constant'
import logUtil from '../../utils/log-util'

function SellerInvitationModal({
  isInvitationSheetOpen,
  closeInvitationSheet,
  sheetContent,
  showReviewContentHandler,
  approvalRequestInfo,
  confirmEmailURL,
  handlerSheetContent,
  getUpdatedSellerInvitation,
  setInviteId,
}) {
  // console.log(approvalRequestInfo)
  const worldReady = useContext(WorldReadyContext)
  const [selectedInvite, setSelectedInvite] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const { csrf, setShowDeveloperInvite } = useApplicationContext()
  const [errorKey, setErrorKey] = useState(null)

  const onCloseAlert = () => {
    recordDynamicEvent(
      `${FPTI_COMPONENTS_CATEGORIES.ALERT_ERROR},click,close-alert,developer-invite`,
    )
    setErrorKey(null)
  }

  const acceptedSheet = async (status, invite) => {
    try {
      let initiatorPartyId = invite?.initiator_party_id
      let requestId = invite?.id
      let approvalNotes = invite?.initiator_notes

      recordCplStart('accept/reject/invitation')
      setIsLoading(true)
      let response = await axiosInstance({
        method: 'post',
        url: '/dashboard/api/developerInvites/updateApproval',
        data: {
          notes: approvalNotes,
          approver_party_id: initiatorPartyId,
          approval_status: status,
          approval_request_id: requestId,
        },
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'x-csrf-token': csrf,
        },
        responseType: 'json',
        contentType: 'application/json',
      })

      recordCplEnd('accept/reject/invitation', {
        cpl_event: 'accept/reject invitation',
        status: 200,
      })
      if (response?.status === 204 && status === 'APPROVED') {
        logUtil.info(`invitation approved successfully`)
        setInviteId(requestId)
        setShowDeveloperInvite(true)
        return 'SUCCESS'
      } else if (
        sheetContent === 'review' &&
        response?.status === 204 &&
        status === 'REJECTED'
      ) {
        logUtil.info(`invitation rejected successfully`)
        handlerSheetContent()
      } else {
        if (response?.status !== 204) {
          logUtil.error(`Unexpected response status: ${response?.status}`)
        }

        if (
          status !== 'APPROVED' &&
          status !== 'REJECTED' &&
          status !== 'CANCELLED'
        ) {
          logUtil.error(`Unexpected status value: ${status}`)
        }

        if (sheetContent !== 'review') {
          logUtil.error(`Unexpected sheetContent value: ${sheetContent}`)
        }
      }
      getUpdatedSellerInvitation(requestId)
    } catch (e) {
      recordCplEnd('accept/reject/invitation', {
        cpl_event: 'accept/reject invitation',
        status: e.response.status || 404,
      })
      setErrorKey('error')
      if (status === 'APPROVED' || status === 'REJECTED') {
        throw e
      }
    } finally {
      setIsLoading(false)
    }
    return {}
  }

  const showReviewContent = (invite) => {
    showReviewContentHandler()
    setSelectedInvite(invite)
  }

  return (
    <Sheet
      onClose={closeInvitationSheet}
      isOpen={isInvitationSheetOpen}
      showLogo={true}
    >
      {isLoading && <Spinner fullScreen={true} size="xl" />}
      {sheetContent === 'initial' && (
        <div className="view_invitation">
          <HeadingText size="lg">Your seller invitations</HeadingText>
          <BodyText as="p">
            Invitations expires 30 days after they're sent. If an invitation
            expires, you can ask the seller to resend it.
          </BodyText>
          {errorKey && (
            <Alert
              className="view_invitation-alert"
              type="warning"
              closeButton={{
                'aria-label': 'dismiss alert',
                onClick: onCloseAlert,
              }}
              data-testid="alert-error"
            >
              <Message id="developer-invite.serverError" />
            </Alert>
          )}
          {approvalRequestInfo.map((invite, index) => {
            let createTime = formatDate(worldReady, invite.create_time)
            let payload = JSON.parse(
              invite?.application_context?.transaction_payload,
            )
            let expiryTime = calculateExpiryTimeInHours(payload.expiryTime)
            let expiryDayHour = calculateExpiryTimeInDaysHours(
              payload.expiryTime,
            )
            let inviterName = payload?.inviterName
            let invitingBusinessName = payload?.invitingBusinessName

            return expiryTime > 0 ? (
              <Card key={index} className="mb-15p0">
                <div className="seller-card">
                  <Badge type="warning">{`Expires in ${expiryDayHour}`}</Badge>
                  <CaptionText>
                    <b>{createTime}</b>
                  </CaptionText>
                </div>
                <Card.Content>
                  <BodyText>
                    <b>{inviterName}</b> invited you to{' '}
                    <b>{invitingBusinessName}</b>
                  </BodyText>
                </Card.Content>
                <Card.Footer>
                  <Button
                    secondary
                    size="sm"
                    className="mr-12p"
                    onClick={() => acceptedSheet('REJECTED', invite)}
                    data-dxa="reject_invite,reject,click_reject_invite"
                    data-testid="rejectInvite"
                  >
                    Decline
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => showReviewContent(invite)}
                    data-dxa="review_accept_invite,review_accept,click_review_accept_invite"
                    data-testid="reviewAcceptInvite"
                  >
                    Review and Accept
                  </Button>
                </Card.Footer>
              </Card>
            ) : (
              <Card className="expired-card">
                <div className="seller-card">
                  <Badge type="critical">Expired</Badge>
                  <CaptionText>
                    <b>{createTime}</b>
                  </CaptionText>
                </div>
                <Card.Content>
                  <BodyText>
                    <b>{inviterName}</b> invited you to{' '}
                    <b>{invitingBusinessName}</b>
                  </BodyText>
                </Card.Content>
                <Card.Footer>
                  <Button
                    secondary
                    size="sm"
                    className="mr-12p"
                    data-dxa="dismiss_invite,dismiss,click_dismiss_invite"
                    data-testid="dismissInvite"
                    onClick={() => acceptedSheet('CANCELLED', invite)}
                  >
                    Dismiss
                  </Button>
                </Card.Footer>
              </Card>
            )
          })}
        </div>
      )}
      {sheetContent === 'review' && (
        <AcceptInvitation
          invite={selectedInvite}
          acceptedSheet={acceptedSheet}
          isLoading={isLoading}
          closeSheet={closeInvitationSheet}
          confirmEmailURL={confirmEmailURL}
        />
      )}
      {sheetContent === 'upgradeRequired' && <UpgradeRequired />}
      <br />
    </Sheet>
  )
}

export default SellerInvitationModal
